import React, { useRef, useEffect, useState } from 'react';

export default function SplitCompare({
  leftLabel  = 'Before',
  leftSub    = 'Manual processing',
  leftNote   = '15+ hours/week · Error-prone',
  rightLabel = 'After — arhatinfo',
  rightSub   = 'AI-powered pipeline',
  rightNote  = 'Minutes · Zero errors · Scales',
  height     = 220,
}) {
  const wrapRef      = useRef(null);
  const clipRef      = useRef(null);
  const divRef       = useRef(null);
  const handleRef    = useRef(null);
  const stateRef     = useRef({ dragging: false, pct: 50, dir: 1, timer: null });

  // measure wrapper so left panel can be pinned to full width
  const [wrapWidth, setWrapWidth] = useState(0);

  useEffect(() => {
    if (!wrapRef.current) return;
    const measure = () => setWrapWidth(wrapRef.current.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // drag + auto-drift
  useEffect(() => {
    const wrap  = wrapRef.current;
    const state = stateRef.current;
    const getX  = e => (e.touches ? e.touches[0].clientX : e.clientX);

    const setPos = p => {
      p = Math.max(5, Math.min(95, p));
      state.pct = p;
      if (clipRef.current)   clipRef.current.style.width  = p + '%';
      if (divRef.current)    divRef.current.style.left    = p + '%';
      if (handleRef.current) handleRef.current.style.left = p + '%';
    };

    const onDown = e => { state.dragging = true; e.preventDefault(); };
    const onMove = e => {
      if (!state.dragging) return;
      const r = wrap.getBoundingClientRect();
      setPos(((getX(e) - r.left) / r.width) * 100);
    };
    const onUp = () => { state.dragging = false; };

    const h = handleRef.current;
    h.addEventListener('mousedown',  onDown);
    h.addEventListener('touchstart', onDown, { passive: false });
    document.addEventListener('mousemove',  onMove);
    document.addEventListener('touchmove',  onMove, { passive: true });
    document.addEventListener('mouseup',    onUp);
    document.addEventListener('touchend',   onUp);

    state.timer = setInterval(() => {
      if (state.dragging) return;
      state.pct += state.dir * 0.22;
      if (state.pct > 65 || state.pct < 35) state.dir *= -1;
      setPos(state.pct);
    }, 30);

    return () => {
      clearInterval(state.timer);
      h.removeEventListener('mousedown',  onDown);
      h.removeEventListener('touchstart', onDown);
      document.removeEventListener('mousemove',  onMove);
      document.removeEventListener('touchmove',  onMove);
      document.removeEventListener('mouseup',    onUp);
      document.removeEventListener('touchend',   onUp);
    };
  }, []);

  return (
    <>
      <style>{`
        .sc-wrap {
          position: relative;
          border-radius: var(--r, 14px);
          overflow: hidden;
          border: 1px solid var(--border-hi, rgba(96,165,250,.35));
          box-shadow: 0 20px 50px rgba(0,0,0,.45), 0 0 60px rgba(59,130,246,.08);
          cursor: ew-resize;
          user-select: none;
        }
        .sc-right {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #0c0e14, #111520);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: .5rem;
        }
        .sc-right::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(96,165,250,.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(96,165,250,.05) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
        }
        .sc-clip {
          position: absolute;
          top: 0; bottom: 0; left: 0;
          overflow: hidden;
          z-index: 2;
        }
        .sc-left {
          /* width set inline from wrapWidth so it never collapses */
          position: absolute;
          top: 0; bottom: 0; left: 0;
          background: linear-gradient(135deg, #161c2e, #1c2236);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: .5rem;
        }
        .sc-badge {
          font-size: .68rem;
          font-weight: 700;
          letter-spacing: .14em;
          text-transform: uppercase;
          position: relative;
          z-index: 1;
        }
        .sc-badge-blue { color: var(--blue-hi, #60a5fa); }
        .sc-badge-dim  { color: rgba(255,255,255,.5); }
        .sc-title {
          font-size: 1.05rem;
          font-weight: 800;
          color: var(--white, #eef2ff);
          letter-spacing: -.02em;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        .sc-note {
          font-size: .76rem;
          color: var(--muted, #8b96b0);
          font-weight: 500;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        .sc-divider {
          position: absolute;
          top: 0; bottom: 0;
          width: 2px;
          background: var(--blue-hi, #60a5fa);
          z-index: 5;
          opacity: .95;
        }
        .sc-handle {
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: var(--blue-hi, #60a5fa);
          border: 2px solid var(--bg, #0c0e14);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          box-shadow: 0 0 24px rgba(59,130,246,.65);
          cursor: ew-resize;
          transition: box-shadow .2s;
        }
        .sc-handle:hover {
          box-shadow: 0 0 40px rgba(59,130,246,.9);
        }
        .sc-arr-l {
          width: 0; height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-right: 6px solid var(--bg, #0c0e14);
        }
        .sc-arr-r {
          width: 0; height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 6px solid var(--bg, #0c0e14);
        }
        .sc-hint {
          font-size: .72rem;
          color: var(--dim, #4a5568);
          text-align: center;
          margin-top: .75rem;
          letter-spacing: .04em;
          font-weight: 500;
        }
      `}</style>

      <div ref={wrapRef} className="sc-wrap" style={{ height }}>

        {/* RIGHT — always visible behind */}
        <div className="sc-right">
          <span className="sc-badge sc-badge-blue">{rightLabel}</span>
          <span className="sc-title">{rightSub}</span>
          <span className="sc-note">{rightNote}</span>
        </div>

        {/* LEFT — clipped by drag position */}
        <div
          ref={clipRef}
          className="sc-clip"
          style={{ width: '50%' }}
        >
          {/* pinned to full wrapper width so content stays centred */}
          <div
            className="sc-left"
            style={{ width: wrapWidth > 0 ? `${wrapWidth}px` : '100%' }}
          >
            <span className="sc-badge sc-badge-dim">{leftLabel}</span>
            <span className="sc-title">{leftSub}</span>
            <span className="sc-note">{leftNote}</span>
          </div>
        </div>

        {/* Divider line */}
        <div ref={divRef} className="sc-divider" style={{ left: '50%' }} />

        {/* Handle */}
        <div ref={handleRef} className="sc-handle" style={{ left: '50%' }}>
          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            <div className="sc-arr-l" />
            <div className="sc-arr-r" />
          </div>
        </div>

      </div>

      <p className="sc-hint">← drag to compare →</p>
    </>
  );
}